import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { ORIGINS, variantLabel } from "../data/products";
import { useRevealAll } from "../hooks/useReveal";
import useStripeProducts from "../hooks/useStripeProducts";

const ORIGIN_FILTERS = [{ value: "all", label: "Összes" }, ...ORIGINS];

const formatHufAmount = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("hu-HU").format(n) + " Ft" : "- Ft";

const priceFromDynamic = (product) => {
  const prices = (product.variants || [])
    .map((v) => v.unit_amount)
    .filter((n) => typeof n === "number");
  if (!prices.length) return "- Ft";
  return formatHufAmount(Math.min(...prices));
};

// Card summary helpers
const variantSummary = (p) => {
  if (!p.variants || p.variants.length === 0) return "";
  if (p.variants.length === 1) {
    const v = p.variants[0];
    return v.length || v.weight ? variantLabel(v) : "Egyedi tétel";
  }
  return `${p.variants.length} méret`;
};
const cardWeightLabel = (p) => {
  if (!p.variants || p.variants.length === 0) return "";
  return p.variants.length === 1 ? (p.variants[0].weight || "Egyedi") : "Több méret";
};
const cardLengthLabel = (p) => {
  if (!p.variants || p.variants.length === 0) return "";
  return p.variants.length === 1
    ? p.variants[0].length || "—"
    : p.variants.map((v) => v.length).filter(Boolean).join(" · ");
};

export default function ProductGrid({ compact = false }) {
  const [params, setParams] = useSearchParams();
  const [origin, setOrigin] = useState(params.get("o") || "all");
  const ref = useRef(null);
  const { products: stripeProducts } = useStripeProducts();
  const PRODUCTS = stripeProducts || [];

  useEffect(() => {
    if (compact) return;
    const next = new URLSearchParams();
    if (origin !== "all") next.set("o", origin);
    setParams(next, { replace: true });
  }, [origin, compact, setParams]);

  const filtered = useMemo(
    () => PRODUCTS.filter((p) => origin === "all" || p.origin === origin),
    [origin, PRODUCTS],
  );

  // Counts for filter UI
  const counts = useMemo(() => {
    const c = { all: PRODUCTS.length };
    PRODUCTS.forEach((p) => {
      c[p.origin] = (c[p.origin] || 0) + 1;
    });
    return c;
  }, [PRODUCTS]);

  // Re-run reveal observer when filter changes so new cards animate in
  useRevealAll(ref, [filtered.length, origin, compact]);

  const items = compact ? filtered.slice(0, 6) : filtered;
  const featured = items[0];
  const rest = items.slice(1);

  return (
    <section
      ref={ref}
      data-testid="product-collection"
      className="py-24 md:py-36 bg-[color:var(--he-bg-alt)] relative"
    >
      <div className="he-container">
        {/* Heading */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20 items-end">
          <div className="md:col-span-7 reveal">
            <p className="overline mb-5 mag-marker">
              <span>Kollekció</span>
            </p>
            <h2
              className="font-serif-display tracking-[-0.022em]"
              style={{
                fontWeight: 400,
                fontSize: "clamp(2.4rem, 5.6vw, 5.4rem)",
                lineHeight: 1.05,
              }}
            >
              Aktuálisan elérhető <br />
              <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                copfok.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 reveal delay-1">
            <div className="flex items-baseline gap-3 mb-4">
              <span className="figure italic text-[color:var(--he-gold)] text-3xl">
                {String(filtered.length).padStart(2, "0")}
              </span>
              <span className="overline-xs text-[color:var(--he-ink-soft)]">
                tétel készleten
              </span>
            </div>
            <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.7] text-[15px]">
              Minden tétel egyedi: konkrét hossz, súly és szín. Ami a képen
              van, az érkezik hozzád. Restock ritkán van — érdemes feliratkozni.
            </p>
            <Link
              to="/kerdoiv"
              data-testid="grid-link-quiz"
              className="btn-ghost mt-5 text-[color:var(--he-ink)]"
            >
              <span>Melyik nekem való?</span>
              <ArrowUpRight size={14} className="ghost-arrow" />
            </Link>
          </div>
        </div>

        {/* Filter strip — origin only */}
        <div className="border-y border-[color:var(--he-line)] py-5 mb-12 md:mb-16 reveal">
          <div className="flex items-center gap-4 md:gap-6">
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto flex-1 min-w-0 -mx-1 px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <span className="overline-xs text-[color:var(--he-ink-soft)] shrink-0 mr-2">
                Eredet
              </span>
              {ORIGIN_FILTERS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setOrigin(o.value)}
                  data-testid={`filter-origin-${o.value}`}
                  className={`he-pill shrink-0 ${origin === o.value ? "active" : ""}`}
                >
                  {o.label}
                  <span className="ml-2 opacity-60" style={{ fontSize: "0.55rem" }}>
                    {String(counts[o.value] || 0).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
            <div
              className="shrink-0 flex items-baseline gap-2 pl-3 md:pl-5 border-l border-[color:var(--he-line)]"
              data-testid="filter-count"
            >
              <span className="figure italic text-[color:var(--he-gold)] text-2xl">
                {String(filtered.length).padStart(2, "0")}
              </span>
              <span className="overline-xs text-[color:var(--he-ink-soft)] hidden sm:inline">
                találat
              </span>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-20 text-center text-[color:var(--he-ink-soft)]">
            <p className="overline mb-4">Nincs találat</p>
            <p className="font-serif-display text-2xl">
              Próbálj másik szűrőt — vagy nézd meg az összes terméket.
            </p>
          </div>
        ) : compact ? (
          // ───── Compact (home page): clean uniform side-by-side grid, grouped by origin ─────
          <>
            <div
              data-testid="compact-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-10 gap-y-14 md:gap-y-20"
            >
              {items.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
            <div className="mt-20 md:mt-24 flex justify-center reveal">
              <Link to="/termekek" data-testid="view-all-products" className="btn-arrow text-[color:var(--he-ink)]">
                <span className="arrow-circle">
                  <ArrowRight size={18} className="arrow-icon" />
                </span>
                <span style={{ fontWeight: 500 }}>Mind a 11 megtekintése</span>
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Featured + first row */}
            {featured && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-14 md:mb-20">
                <FeaturedCard product={featured} index={0} className="md:col-span-7" />
                <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-8 md:gap-10">
                  {rest.slice(0, 2).map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i + 1} small />
                  ))}
                </div>
              </div>
            )}

            {/* Editorial break */}
            {rest.length > 2 && (
              <EditorialBreak
                left="Tudnivaló"
                main="Minden tételt magunk válogatunk és ellenőrzünk — egyetlen donortól, vegyítés nélkül."
                right="Tételek"
                value={String(filtered.length).padStart(2, "0")}
              />
            )}

            {/* Dense 3-column grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-10 gap-y-14 md:gap-y-20">
              {rest.slice(2).map((p, i) => (
                <ProductCard key={p.id} product={p} index={i + 3} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function FeaturedCard({ product, index, className = "" }) {
  return (
    <Link
      to={`/termek/${product.stripe_product_id}/${product.slug}`}
      data-testid={`product-card-${product.stripe_product_id}`}
      className={`group he-card flex flex-col reveal ${className}`}
    >
      <div className="he-img-wrap aspect-[5/6]">
        <img
          src={product.image}
          alt={product.name}
          className="he-img w-full h-full object-cover"
          loading="lazy"
        />
        {/* Featured overlay */}
        <div className="absolute top-5 left-5 px-3 py-1.5 bg-[color:var(--he-gold)] text-[color:var(--he-ink)] flex items-center gap-2">
          <span className="overline-xs" style={{ fontSize: "0.55rem", letterSpacing: "0.28em" }}>
            Készleten
          </span>
        </div>
        <div className="absolute top-5 right-5 figure italic text-white text-3xl">
          {String(index + 1).padStart(2, "0")}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/75 via-black/30 to-transparent text-white">
          <div className="flex items-end justify-between">
            <div>
              <p className="overline-xs text-white/80 mb-1.5" style={{ fontSize: "0.58rem" }}>
                {product.origin === "europai" ? "Európai" : "Kazahsztáni"}
              </p>
              <h3
                className="font-serif-display text-2xl md:text-3xl leading-[1.1]"
                style={{ fontWeight: 400 }}
              >
                {product.name}
              </h3>
            </div>
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/30 group-hover:bg-[color:var(--he-gold)] group-hover:border-[color:var(--he-gold)] transition-all duration-500">
              <ArrowUpRight size={16} />
            </span>
          </div>
        </div>
      </div>
      {/* Quick spec strip below */}
      <div className="mt-5 grid grid-cols-3 border-t border-[color:var(--he-line)] pt-4">
        <SpecMini label="Súly" value={cardWeightLabel(product) || "—"} />
        <SpecMini label="Hossz" value={cardLengthLabel(product) || "—"} />
        <SpecMini label="Ár-tól" value={priceFromDynamic(product)} highlight align="right" />
      </div>
    </Link>
  );
}

function ProductCard({ product, index, small = false }) {
  const hasNamedVariants = product.variants && product.variants.length > 0 &&
    (product.variants[0].length || product.variants[0].weight);
  return (
    <Link
      to={`/termek/${product.stripe_product_id}/${product.slug}`}
      data-testid={`product-card-${product.stripe_product_id}`}
      className="group he-card flex flex-col reveal"
    >
      <div className={`he-img-wrap ${small ? "aspect-[4/5]" : "aspect-[3/4]"}`}>
        <img
          src={product.image}
          alt={product.name}
          className="he-img w-full h-full object-cover"
          loading="lazy"
        />
        <span className="absolute top-4 left-4 figure italic text-[color:var(--he-gold)] text-2xl md:text-3xl drop-shadow">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="absolute top-5 right-4 overline-xs text-white px-2.5 py-0.5 border border-white/45 backdrop-blur-sm bg-black/20" style={{ fontSize: "0.55rem" }}>
          {product.origin === "europai" ? "EU" : "KZ"}
        </span>

        {/* Hover bottom info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 card-meta-hover bg-gradient-to-t from-black/75 via-black/35 to-transparent text-white">
          <div className="flex items-center justify-between text-xs uppercase" style={{ letterSpacing: "0.22em" }}>
            <span>{cardWeightLabel(product) || variantSummary(product)}</span>
            <span className="inline-flex items-center gap-2">
              Megnézem <ArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="overline-xs text-[color:var(--he-ink-soft)] mb-1.5" style={{ fontSize: "0.58rem" }}>
            {product.origin === "europai" ? "Európai" : "Kazahsztáni"}
          </p>
          <h3
            className="font-serif-display text-lg md:text-[1.3rem] leading-[1.18]"
            style={{ fontWeight: 400 }}
          >
            {product.name}
          </h3>
          <p className="mt-1.5 text-xs text-[color:var(--he-ink-soft)] font-light leading-relaxed line-clamp-2">
            {variantSummary(product)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif-display text-base md:text-lg" style={{ fontWeight: 400 }}>
            {priceFromDynamic(product)}
          </p>
        </div>
      </div>

      {/* Variant quick-pills — only when there are named variants */}
      {hasNamedVariants && (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {product.variants.map((v, i) => (
            <span
              key={`${v.length}-${v.weight}-${i}`}
              className="text-[10px] uppercase text-[color:var(--he-ink-soft)] border border-[color:var(--he-line)] px-2 py-0.5 rounded-full group-hover:border-[color:var(--he-gold)]/50 transition-colors"
              style={{ letterSpacing: "0.18em" }}
            >
              {variantLabel(v)}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

function SpecMini({ label, value, highlight, align }) {
  return (
    <div className={align === "right" ? "text-right" : ""}>
      <p className="overline-xs text-[color:var(--he-ink-soft)]" style={{ fontSize: "0.55rem" }}>
        {label}
      </p>
      <p
        className={`mt-1 text-sm font-light ${highlight ? "font-serif-display text-base" : ""}`}
        style={highlight ? { fontWeight: 400 } : {}}
      >
        {value}
      </p>
    </div>
  );
}

function EditorialBreak({ left, main, right, value }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center mb-14 md:mb-20 py-6 border-y border-[color:var(--he-line)] reveal">
      <div className="md:col-span-3">
        <p className="overline-xs text-[color:var(--he-gold)]">{left}</p>
      </div>
      <div className="md:col-span-7">
        <p
          className="font-serif-display italic text-xl md:text-2xl text-[color:var(--he-ink)]"
          style={{ fontWeight: 300 }}
        >
          „{main}"
        </p>
      </div>
      <div className="md:col-span-2 text-right flex md:justify-end items-baseline gap-3">
        <span className="overline-xs text-[color:var(--he-ink-soft)]">{right}</span>
        <span className="figure italic text-[color:var(--he-gold)] text-2xl">{value}</span>
      </div>
    </div>
  );
}
